import type { ProductOwnerActivityHour } from '../stores/product_owner_runs'
import type { HeatmapCell, HeatmapWeek } from './productOwnerActivityHeatmap'

// Grid-building logic for the Activity tab's per-repo hourly drill-down
// (#1210), split out of the Vue component so it's unit-testable without
// mounting anything — same split as productOwnerActivityHeatmap.ts. Reuses
// that module's HeatmapCell/HeatmapWeek shapes (and, in the component, its
// color/title helpers) rather than inventing a parallel set: a cell is a
// cell regardless of whether it represents a day or an hour.

const HOURS_PER_DAY = 24

function isoHourKey(d: Date): string {
  // `d` is always already truncated to the hour, so this only needs to drop
  // the milliseconds toISOString() adds — matches Ruby's Time#iso8601.
  return `${d.toISOString().slice(0, 19)}Z`
}

function formatHourLabel(d: Date): string {
  const date = d.toISOString().slice(0, 10)
  const hour = String(d.getUTCHours()).padStart(2, '0')
  return `${date} ${hour}:00 UTC`
}

// Builds one column per UTC calendar day within the window (oldest first),
// each holding 24 hourly cells — mirrors buildHeatmapWeeks' column-per-week
// shape so the same cell color/title helpers apply unmodified. `windowHours`
// is one of ACTIVITY_HOURLY_ALLOWED_WINDOW_HOURS from the agent-box
// controller (24/72/168); a partial leading day is padded with null cells
// for any hour before the window's actual start.
export function buildHourlyHeatmapDays(
  hours: ProductOwnerActivityHour[],
  windowHours: number,
  now: Date = new Date(),
): HeatmapWeek[] {
  const byHour = new Map(hours.map((h) => [h.hour, h]))

  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours()))
  const start = new Date(end)
  start.setUTCHours(start.getUTCHours() - (windowHours - 1))
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()))
  const endDayStart = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  // A window's start/end hours rarely land on a day boundary, so the day
  // span is computed from the actual calendar days it touches — a 24-hour
  // window ending mid-afternoon spans TWO calendar days, not windowHours/24.
  const dayCount = Math.round((endDayStart - cursor.getTime()) / (HOURS_PER_DAY * 60 * 60 * 1000)) + 1

  const days: HeatmapWeek[] = []
  for (let d = 0; d < dayCount; d++) {
    const cells: (HeatmapCell | null)[] = []
    for (let h = 0; h < HOURS_PER_DAY; h++) {
      if (cursor < start || cursor > end) {
        cells.push(null)
      } else {
        const match = byHour.get(isoHourKey(cursor))
        cells.push({
          date: formatHourLabel(cursor),
          totalRuns: match?.total_runs ?? 0,
          idlePercentage: match ? match.idle_percentage : null,
        })
      }
      cursor.setUTCHours(cursor.getUTCHours() + 1)
    }
    days.push({ cells })
  }
  return days
}
